import parser from 'any-date-parser';

import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  CreateTherapyRequestBody,
  Therapy,
  UpdateTherapyRequest,
} from '@interfaces/therapies.interface';

import {
  useCreateTherapyMutation,
  useUpdateTherapyMutation,
} from '@api/therapies.api';

export const UPSERT_THERAPY_FORM_MODAL_ID = 'upsertTherapy';

interface UpsertTherapyFormProps {
  therapy?: Therapy;
  patientId: string;
  afterSubmit?: (newTherapy: Therapy) => void;
}

export default function UpsertTherapyForm({
  therapy,
  patientId,
  afterSubmit,
}: UpsertTherapyFormProps) {
  const initialValues = {
    description: therapy?.description || '',
    startDate: therapy?.startDate ? new Date(therapy.startDate) : new Date(),
    endDate: therapy?.endDate ? new Date(therapy.endDate) : undefined,
  };

  const form = useForm({
    initialValues,
  });

  const [createTherapy, { isLoading: isLoadingCreate }] =
    useCreateTherapyMutation();
  const [updateTherapy, { isLoading: isLoadingUpdate }] =
    useUpdateTherapyMutation();

  const onCancelClick = () => {
    closeModal(UPSERT_THERAPY_FORM_MODAL_ID);
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      let newTherapy;

      const mappedValues = {
        description: values.description,
        startDate: new Date(values.startDate).toISOString(),
        endDate: values.endDate ? new Date(values.endDate).toISOString() : null,
      };

      if (therapy) {
        const data: UpdateTherapyRequest = {
          id: therapy.id,
          body: mappedValues,
        };

        newTherapy = await updateTherapy(data).unwrap();

        notifications.show({
          title: 'Terapia modificata',
          message: 'Terapia modificata con successo.',
        });
      } else {
        const data: CreateTherapyRequestBody = {
          ...mappedValues,
          patientId,
        };

        newTherapy = await createTherapy(data).unwrap();

        notifications.show({
          title: 'Terapia creata',
          message: 'Terapia creata con successo.',
        });
      }

      closeModal(UPSERT_THERAPY_FORM_MODAL_ID);

      afterSubmit?.(newTherapy);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm">
        <TextInput
          data-autofocus
          required
          label="Descrizione"
          {...form.getInputProps('description')}
        />
        <DateInput
          required
          label="Data di inizio"
          valueFormat="DD/MM/YYYY"
          dateParser={(value) => parser.fromString(value, 'it')}
          {...form.getInputProps('startDate')}
        />
        <DateInput
          label="Data di fine"
          valueFormat="DD/MM/YYYY"
          clearable
          dateParser={(value) => parser.fromString(value, 'it')}
          {...form.getInputProps('endDate')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="xs" ta="right">
          {form.errors.general}
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
